import React from 'react';

export interface LogoProps {
  variant?: 'lockup' | 'stacked' | 'mark' | 'favicon';
  size?: number;
  tagline?: string;
  light?: 'beams' | 'arcs';
  beams?: boolean;
  style?: React.CSSProperties;
}

const s = (v?: React.CSSProperties): React.CSSProperties => ({ display: 'block', flexShrink: 0, ...v });

function Mark({ size = 40, light = 'beams' }: { size?: number; light?: 'beams' | 'arcs' }) {
  const a = 'var(--logo-accent)';
  const body = 'var(--logo-body)';
  const detail = 'var(--logo-detail)';
  const shade = 'var(--logo-shade)';

  return (
    <svg viewBox="0 0 120 120" width={size} height={size} fill="none" style={s()} aria-hidden="true">
      {light === 'beams' && (
        <g>
          <path d="M60 42 L116 18 L116 40 Z" fill={a} opacity="0.22" />
          <path d="M60 44 L116 50 L116 72 Z" fill={a} opacity="0.12" />
          <path d="M60 42 L18 30 L18 44 Z" fill={a} opacity="0.14" />
        </g>
      )}
      {light === 'arcs' && (
        <g stroke={a} strokeWidth="4.6" fill="none" strokeLinecap="round">
          <path d="M86.5 18 A 32 32 0 0 1 86.5 50" />
          <path d="M102 9 A 49 49 0 0 1 102 59" />
          <path d="M33.5 18 A 32 32 0 0 0 33.5 50" />
          <path d="M18 9 A 49 49 0 0 0 18 59" />
        </g>
      )}
      <path d="M46 28 L60 12 L74 28 Z" fill={a} />
      <rect x="49" y="28" width="22" height="12" fill={body} />
      <rect x="54" y="30" width="12" height="8" fill={a} />
      <rect x="46" y="40" width="28" height="4" rx="1" fill={detail} />
      <path d="M50 44 L44 104 L76 104 L70 44 Z" fill={body} />
      <path d="M48.8 56 L47.6 70 L72.4 70 L71.2 56 Z" fill={a} />
      <path d="M55 104 v-14 a5 5 0 0 1 10 0 v14 Z" fill={shade} />
      <path d="M40 104 L80 104 L78 112 L42 112 Z" fill={detail} />
    </svg>
  );
}

function Favicon({ size = 40 }: { size?: number }) {
  const a = 'var(--logo-accent)';
  const body = 'var(--logo-body)';

  return (
    <svg viewBox="0 0 64 64" width={size} height={size} fill="none" style={s()} aria-hidden="true">
      <g stroke={a} strokeWidth="2.6" fill="none" strokeLinecap="round">
        <path d="M46 11 A 17 17 0 0 1 46 28" />
        <path d="M53 5 A 26 26 0 0 1 53 34" />
        <path d="M18 11 A 17 17 0 0 0 18 28" />
        <path d="M11 5 A 26 26 0 0 0 11 34" />
      </g>
      <path d="M24 16 L32 6 L40 16 Z" fill={a} />
      <rect x="25" y="16" width="14" height="7" fill={body} />
      <rect x="28.5" y="17.5" width="7" height="4" fill={a} />
      <path d="M26 23 L22 54 L42 54 L38 23 Z" fill={body} />
      <path d="M25.2 33 L24.4 42 L39.6 42 L38.8 33 Z" fill={a} />
    </svg>
  );
}

export function Logo({
  variant = 'lockup',
  size = 40,
  tagline,
  light = 'beams',
  beams,
  style,
}: LogoProps) {
  const l = beams === false ? 'arcs' : light;
  if (variant === 'mark') {
    return (
      <span style={{ display: 'inline-flex', ...style }}>
        <Mark size={size} light={l} />
      </span>
    );
  }
  if (variant === 'favicon') {
    return (
      <span style={{ display: 'inline-flex', ...style }}>
        <Favicon size={size} />
      </span>
    );
  }
  const stacked = variant === 'stacked';
  return (
    <span
      style={{
        display: 'inline-flex',
        flexDirection: stacked ? 'column' : 'row',
        alignItems: 'center',
        gap: stacked ? size * 0.14 : size * 0.18,
        ...style,
      }}
    >
      <Mark size={size} light={l} />
      <span
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: size * 0.08,
          alignItems: stacked ? 'center' : 'flex-start',
        }}
      >
        <span
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 'var(--fw-bold)',
            letterSpacing: '-0.02em',
            color: 'var(--text-primary)',
            fontSize: size * 0.64,
            lineHeight: 1,
          }}
        >
          CYRN
        </span>
        {tagline && (
          <span
            style={{
              fontFamily: 'var(--font-body)',
              fontWeight: 'var(--fw-medium)',
              letterSpacing: '0.02em',
              color: 'var(--text-secondary)',
              fontSize: Math.max(11, size * 0.17),
              lineHeight: 1.2,
            }}
          >
            {tagline}
          </span>
        )}
      </span>
    </span>
  );
}
